import { useState, useEffect, useMemo } from 'react';
import * as echarts from 'echarts';
import { notification } from 'tea-component';
import {
  getStudioFinancialsByMonth,
  getStudioFinancialsByYear,
} from '@sg/services/index';
import { chartConfig } from '@sg/utils/chatUtils';
import { chartGradientColors, chartAllColors } from '@sg/utils/useChartOptions';

export const useFinancialsData = (
  studioId: string,
  activeDateTypeSegment: string,
  financialChartType: string,
  financialShowSymbol: boolean,
  selectedMonth?: string | null,
) => {
  const [financialsTableData, setFinancialsTableData] = useState<any[]>([]);
  const [studioFinancialUpdateTime, setStudioFinancialUpdateTime] = useState<string>('');
  const [financialsLoading, setFinancialsLoading] = useState<boolean>(false);
  const showFinancialsList = ['bar', 'table'];

  useEffect(() => {
    if (!studioId) {
      setFinancialsTableData([]);
      setStudioFinancialUpdateTime('');
      setFinancialsLoading(false);
      return;
    }

    const fetchData = async () => {
      setFinancialsLoading(true);
      // Clear before fetch so a null datatime from the new segment does not keep the old value.
      setStudioFinancialUpdateTime('');
      try {
        const payload: { studio_id: string; datatime?: string } = { studio_id: studioId };
        if (selectedMonth) {
          payload.datatime = selectedMonth;
        }
        const res = activeDateTypeSegment === 'year'
          ? await getStudioFinancialsByYear(payload)
          : await getStudioFinancialsByMonth(payload);
        const rawRows = res?.data?.data || [];

        setStudioFinancialUpdateTime(res?.data?.datatime ?? '');

        const isYearMode = activeDateTypeSegment === 'year';
        const processedData = rawRows
          .map((item: any) => ({
            date: isYearMode ? item?.year : item?.month,
            Revenue: item?.revenue,
            Profit: item?.profit,
            Profitability: item?.profitability !== null && item?.profitability !== undefined
              ? parseFloat(item.profitability.toFixed(1))
              : item?.profitability,
          }))
          .filter((item: any) => item?.date !== null && item?.date !== undefined && item?.date !== '')
          .sort((a: any, b: any) => (
            isYearMode
              ? Number(a.date) - Number(b.date)
              : a.date.localeCompare(b.date)
          ));

        setFinancialsTableData(processedData);
      } catch (error) {
        console.error('Error fetching studio financials data:', error);
        notification.error({
          description: 'Failed to fetch studio financials data'
        });
        setStudioFinancialUpdateTime('');
      } finally {
        setFinancialsLoading(false);
      }
    };

    fetchData();
  }, [studioId, activeDateTypeSegment, selectedMonth]);

  const financialsData = useMemo(() => {
    const yAxisConfig = [
      {
        type: 'value',
        position: 'left',
        alignTicks: true,
      },
      {
        type: 'value',
        name: 'Profitability',
        position: 'right',
        alignTicks: true,
        axisLine: {
          lineStyle: {
            type: 'solid',
            color: '#A2A4B1',
            width: 1,
          },
        },
        axisLabel: {
          formatter: '{value}%'
        }
      }
    ];

    return chartConfig(financialsTableData, showFinancialsList, (data: any) => {
      const datas = data;
      const chatsNamesObj: any = {
        Revenue: 'Gross Revenue'
      };
      return Object.keys(datas).map((keys, index) => {
        const tdata: any = {
          name: chatsNamesObj[keys] || keys,
          barGap: '0%',
          barWidth: '40%',
          yAxisIndex: keys === 'Profitability' ? 1 : 0,
          color: chartAllColors[1 - index],
          data: datas[keys].map((items: any) => {
            if (financialChartType === 'bar' && keys !== 'Profitability') {
              const numericValue = Number(items);
              return {
                value: items,
                label: {
                  position: Number.isFinite(numericValue) && numericValue < 0
                    ? 'bottom'
                    : 'top',
                },
                itemStyle: {
                  color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                    { offset: 0, color: chartGradientColors[1 - index]?.[1] },
                    { offset: 1, color: chartGradientColors[1 - index]?.[0] }
                  ]),
                }
              };
            }
            if (financialChartType === 'bar' && keys === 'Profitability') {
              return {
                value: items,
                valueType: 'percent',
              };
            }
            return items;
          }),
          type: keys === 'Profitability' && financialChartType === 'bar'
            ? 'line'
            : financialChartType,
          z: keys === 'Profitability' ? 99 : Object.keys(datas).length - index,
          showSymbol: financialShowSymbol,
        };
        return tdata;
      });
    }, yAxisConfig as any);
  }, [financialsTableData, financialChartType, financialShowSymbol]);

  return {
    financialsData,
    financialsTableData,
    studioFinancialUpdateTime,
    financialsLoading,
  };
};
